import React, { useState } from 'react';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer/index';
import Diskon from '../../components/layanan/walkie_talkie/paket';
import { useDarkMode } from '../../components/darkmode/DarkModeContext'; // Import the context
import { Radio, Zap, MapPin, Users, Shield, CheckCircle, MessageCircle, ArrowRight, Battery, Wifi } from 'lucide-react';

const WalkieTalkiePage = () => {
  // Remove local dark mode state and use context instead
  const { isDarkMode } = useDarkMode();

  const htFeatures = [
    {
      icon: <Radio className="w-8 h-8" />,
      title: "Komunikasi Instan",
      description: "Komunikasi dua arah yang cepat dan langsung tanpa delay dengan push-to-talk",
      color: "bg-blue-500"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Jangkauan Luas",
      description: "Mencapai area yang sulit dijangkau oleh sinyal telepon seluler",
      color: "bg-green-500"
    },
    {
      icon: <Battery className="w-8 h-8" />,
      title: "Battery Tahan Lama",
      description: "Baterai dengan daya tahan tinggi untuk operasional sepanjang hari",
      color: "bg-purple-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Keamanan Komunikasi",
      description: "Komunikasi terenkripsi untuk percakapan yang aman dan privat",
      color: "bg-yellow-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multi-User Channel",
      description: "Mendukung multiple channel untuk tim yang berbeda",
      color: "bg-red-500"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Tahan Banting",
      description: "Desain rugged dan tahan terhadap kondisi lapangan yang berat",
      color: "bg-indigo-500"
    }
  ];

  const applications = [
    {
      title: "Security & Patrol",
      description: "Untuk tim keamanan yang membutuhkan koordinasi cepat di area luas",
      image: "/image/layanan/ht-security.png",
      benefits: ["Koordinasi tim", "Response cepat", "Monitoring real-time"]
    },
    {
      title: "Event Management",
      description: "Mengelola acara besar dengan komunikasi yang efisien antar panitia",
      image: "/image/layanan/ht-event.png",
      benefits: ["Koordinasi area", "Emergency response", "Efisiensi operasional"]
    },
    {
      title: "Construction Site",
      description: "Komunikasi di lokasi konstruksi dengan kondisi noise tinggi",
      image: "/image/layanan/ht-construction.png",
      benefits: ["Noise cancellation", "Durable design", "Safety communication"]
    },
    {
      title: "Hospitality",
      description: "Staff hotel, resort, dan fasilitas hospitality lainnya",
      image: "/image/layanan/ht-hotel.png",
      benefits: ["Service coordination", "Guest requests", "Multi-department"]
    }
  ];

  const productTypes = [
    {
      type: "HT Standard",
      range: "3-5 km",
      channels: "16 Channel",
      features: ["LCD Display", "VOX", "Scan", "Keypad Lock"],
      bestFor: "Operasional sehari-hari"
    },
    {
      type: "HT Professional",
      range: "5-10 km",
      channels: "128 Channel",
      features: ["GPS", "Bluetooth", "NOAA Weather", "Long Battery"],
      bestFor: "Tim profesional dan outdoor"
    },
    {
      type: "HT Heavy Duty",
      range: "10-15 km",
      channels: "256 Channel",
      features: ["Military Standard", "Waterproof", "Explosion Proof", "Encryption"],
      bestFor: "Lingkungan ekstrem dan industri"
    },
    {
      type: "HT Digital",
      range: "8-12 km",
      channels: "1000+ Channel",
      features: ["Digital Signal", "Text Messaging", "GPS Tracking", "Group Call"],
      bestFor: "Enterprise dan organisasi besar"
    }
  ];

  const packages = [
    {
      id: 1,
      name: "Starter Pack",
      popular: false,
      color: "from-blue-500 to-blue-700",
      units: "4 Unit HT",
      includes: [
        "4x HT Standard",
        "4x Charger",
        "4x Battery",
        "4x Antenna",
        "Gratis konsultasi",
        "Garansi 1 tahun"
      ]
    },
    {
      id: 2,
      name: "Team Pack",
      popular: true,
      color: "from-green-500 to-green-700",
      units: "8 Unit HT",
      includes: [
        "8x HT Professional",
        "2x Charging Rack",
        "16x Battery",
        "8x Antenna",
        "8x Belt Clip",
        "Training penggunaan",
        "Garansi 2 tahun"
      ]
    },
    {
      id: 3,
      name: "Enterprise Pack",
      popular: false,
      color: "from-purple-500 to-purple-700",
      units: "16+ Unit HT",
      includes: [
        "HT Digital Grade",
        "Multi Charging Station",
        "Spare Batteries",
        "Repeater System",
        "Advanced Training",
        "Maintenance Contract",
        "24/7 Support",
        "Garansi 3 tahun"
      ]
    }
  ];

  const testimonials = [
    {
      name: "Budi Santoso",
      role: "Security Manager",
      company: "PT. Sentral Plaza",
      rating: 5,
      comment: "HT dari Motorola sangat reliable untuk tim security kami. Baterai tahan seharian penuh.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Diana Sari",
      role: "Event Coordinator",
      company: "Event Organizer",
      rating: 5,
      comment: "Untuk event besar, komunikasi HT sangat krusial. Produk dan layanannya memuaskan.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Ahmad Rizki",
      role: "Construction Manager",
      company: "PT. Bangun Jaya",
      rating: 5,
      comment: "HT tahan banting sangat cocok untuk lingkungan konstruksi yang keras dan berdebu.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    }
  ];

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (packageName = "Walkie Talkie HT") => {
    const phoneNumber = "6287778882022";
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'
    }`}>
      
      {/* Navigation */}
      <Navbar />
      
      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : ''
        }`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/image/ht-hero-dark.jpg')"
            : "linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url('/image/ht-hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${
              isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
            } px-4 py-2 rounded-full text-sm font-medium mb-6`}>
              <Radio className="w-4 h-4" />
              Handy Talky Solutions
            </div>

            <h1
              className={`text-4xl md:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Penyediaan Perangkat
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
                Walkie Talkie/Handy Talky (HT)
              </span>
            </h1>

            <p
              className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Kami menyediakan perangkat komunikasi radio dua arah (HT) yang handal untuk 
              mendukung komunikasi instan di lingkungan kerja yang membutuhkan mobilitas tinggi.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => contactViaWhatsApp("Walkie Talkie HT")}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </div>
      </section>

       <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Keunggulan Handy Talky Kami
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Solusi komunikasi yang andal untuk berbagai kebutuhan operasional
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {htFeatures.map((feature, index) => (
              <div
                key={index}
                className={`
                  group p-6 rounded-xl shadow-lg overflow-hidden 
                  transform hover:scale-105 hover:shadow-2xl transition-all duration-300
                  ${isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'
                  }
                `}
              >
                <div
                  className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4
                    ${feature.color || 'bg-[#1E9B4C]'}
                  `}
                >
                  {feature.icon}
                </div>

                <h3
                  className={`text-xl font-bold mb-3 transition-colors ${
                    isDarkMode
                      ? 'text-white group-hover:text-[#1E9B4C]'
                      : 'text-gray-900 group-hover:text-[#1E9B4C]'
                  }`}
                >
                  {feature.title}
                </h3>

                <p
                  className={`transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 group-hover:text-gray-200' 
                      : 'text-gray-600 group-hover:text-gray-700'
                  }`}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Berbagai Aplikasi Penggunaan
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Solusi tepat untuk berbagai kebutuhan komunikasi operasional
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {applications.map((application, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={application.image}
                    alt={application.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3
                    className={`text-xl font-bold mb-3 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {application.title}
                  </h3>
                  <p
                    className={`mb-4 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {application.description}
                  </p>
                  <div className="space-y-2">
                    {application.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Types Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Jenis Produk Handy Talky
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Pilihan beragam sesuai dengan kebutuhan dan budget Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productTypes.map((product, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50 hover:shadow-xl hover:bg-gray-750' 
                    : 'bg-gray-50 border-gray-200 shadow-gray-300/20 hover:shadow-xl hover:bg-white'
                }`}
              >
                <h3 className={`font-bold text-lg mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {product.type}
                </h3>
                
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-[#1E9B4C]" />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Jangkauan: {product.range}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-[#1E9B4C]" />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Channel: {product.channels}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Fitur Utama:
                  </h4>
                  <ul className="space-y-1">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        • {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                  <p className={`text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-green-700'}`}>
                    Cocok untuk: {product.bestFor}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Updated with Horizontal Mobile Scroll */}
     <Diskon />

      <section
    className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
    }`}
    >
    <div className="container mx-auto text-center">
        <h2 className="text-3xl font-bold text-white mb-4">
        Siap Tingkatkan Komunikasi Tim Anda?
        </h2>
        <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
        Dapatkan konsultasi gratis dari ahli kami untuk solusi Walkie Talkie/Handy Talky (HT) yang tepat untuk kebutuhan operasional Anda.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
            onClick={() => contactViaWhatsApp("Walkie Talkie HT")}
            className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
        >
            <MessageCircle className="w-5 h-5" />
            Konsultasi Gratis
        </button>
        <button
            onClick={() => contactViaWhatsApp("Walkie Talkie Enterprise")}
            className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
        >
            <ArrowRight className="w-5 h-5" />
            Hubungi Sales
        </button>
        </div>
    </div>
    </section>
      {/* Footer */}
    <Footer />
    </div>
  );
};

export default WalkieTalkiePage;